import type { DifferenceCardThumbnailStatus } from './DifferenceCardThumbnail';

export const GRAPHIC_THUMBNAIL_MAX_RETRIES = 20;
export const GRAPHIC_THUMBNAIL_INITIAL_DELAY_MS = 500;
export const GRAPHIC_THUMBNAIL_BASE_DELAY_MS = 200;

type GraphicThumbnailImage = Pick<HTMLImageElement, 'src' | 'onload' | 'onerror' | 'crossOrigin'>;

export const graphicThumbnailRetryDelayMs = (retryCount: number): number =>
  retryCount === 0 ? GRAPHIC_THUMBNAIL_INITIAL_DELAY_MS : GRAPHIC_THUMBNAIL_BASE_DELAY_MS * 2 ** (retryCount - 1);

export const graphicThumbnailStatus = ({
  documentHasImage,
  isReady,
  isFailed,
}: {
  documentHasImage: boolean;
  isReady: boolean;
  isFailed: boolean;
}): DifferenceCardThumbnailStatus => {
  if (!documentHasImage) return 'noComparison';
  if (isFailed) return 'error';
  if (isReady) return 'ready';
  return 'loading';
};

export type LoadGraphicThumbnailOptions = {
  thumbnailPath: string;
  useCors: boolean;
  maxRetries?: number;
  retryDelayMs?: (retryCount: number) => number;
  scheduleRetry: (fn: () => void, delay: number) => void;
  onReady: () => void;
  onFailed: () => void;
  isCancelled: () => boolean;
  ImageConstructor?: new () => GraphicThumbnailImage;
};

export const loadGraphicThumbnail = ({
  thumbnailPath,
  useCors,
  maxRetries = GRAPHIC_THUMBNAIL_MAX_RETRIES,
  retryDelayMs = graphicThumbnailRetryDelayMs,
  scheduleRetry,
  onReady,
  onFailed,
  isCancelled,
  ImageConstructor = Image,
}: LoadGraphicThumbnailOptions): void => {
  const attempt = (retryCount: number) => {
    const img = new ImageConstructor();
    if (useCors) img.crossOrigin = 'use-credentials';

    img.onload = () => {
      if (isCancelled()) return;
      onReady();
    };

    img.onerror = () => {
      if (isCancelled()) return;
      if (retryCount < maxRetries) {
        scheduleRetry(() => {
          if (isCancelled()) return;
          attempt(retryCount + 1);
        }, retryDelayMs(retryCount));
        return;
      }
      onFailed();
    };

    img.src = thumbnailPath;
  };

  attempt(0);
};
